import { FC } from 'react';
import {
  DATA_ROOM_PERMISSION_LOCKED,
  DataRoomAccessPermission,
  effectiveDataRoomPermission,
} from 'shared';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';

/**
 * The segmented View / View + Download control of the Invite flow. Separate from the Manage access
 * dropdown on purpose — that one edits a stored permission and waits for Save, this one picks the
 * permission a person will be granted and has nothing to save yet.
 */
export const InvitePermissionToggle: FC<{
  onChange: (permission: DataRoomAccessPermission) => void;
  value: DataRoomAccessPermission;
}> = ({ onChange, value: pickedValue }) => {
  // While the lock is on the segment is shown as it will be granted, and pressing either does
  // nothing: the permission the Invite flow sends is not the one the room will honour.
  const value = effectiveDataRoomPermission(pickedValue);
  // Each segment holds its own width out of the design's 214, rather than taking the width of
  // whatever it currently says: the selected label is drawn half a weight heavier, so a group
  // sized to its content changes width on every switch and the row shifts under the pointer.
  const options = [
    { id: 'VIEW' as const, permission: DataRoomAccessPermission.VIEWER, width: 'w-[68px]' },
    {
      id: 'VIEW_DOWNLOAD' as const,
      permission: DataRoomAccessPermission.VIEWER_DOWNLOAD,
      width: 'w-[146px]',
    },
  ];

  return (
    <div
      className={twMerge(
        'flex h-9 w-[214px] shrink-0 overflow-hidden rounded border border-gray-200 shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)]',
        // Muted as a whole rather than per segment: greying the labels would take the selected
        // one down with them and lose which permission is being granted.
        DATA_ROOM_PERMISSION_LOCKED && 'opacity-60',
      )}
    >
      {options.map(({ id, permission, width }, index) => (
        <button
          className={twMerge(
            // Never two lines: the group is drawn 214 wide and 36 tall, and a wrapped label would
            // push it past the row it sits in.
            'whitespace-nowrap py-2 text-center text-sm leading-5',
            width,
            index > 0 && 'border-l border-gray-200',
            value === permission
              ? 'bg-gray-50 font-[550] text-gray-700'
              : 'bg-white font-normal text-gray-500',
            DATA_ROOM_PERMISSION_LOCKED && 'cursor-default',
          )}
          disabled={DATA_ROOM_PERMISSION_LOCKED}
          key={id}
          onClick={() => onChange(permission)}
          type="button"
        >
          <AppFormattedMessage id={id} />
        </button>
      ))}
    </div>
  );
};
